// src/pages/Home.jsx
import React, { useState } from 'react';
import { Tag, Medal, Star, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select';
import '../styles/custom.css';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState(null);

  // Mock data for restaurant search
  const mockRestaurants = [
    { id: 1, name: "Al-Madina Grill", cuisine: "Middle Eastern", location: "New York" },
    { id: 2, name: "Istanbul Kitchen", cuisine: "Turkish", location: "Brooklyn" },
    { id: 3, name: "Kabab Express", cuisine: "Pakistani", location: "Queens" },
    { id: 4, name: "Halal Guys", cuisine: "American", location: "Manhattan" },
    { id: 5, name: "Sabroso Halal", cuisine: "Mexican", location: "Brooklyn" },
    { id: 6, name: "Salam Restaurant", cuisine: "Lebanese", location: "New York" },
    { id: 7, name: "Naya Express", cuisine: "Mediterranean", location: "Manhattan" },
    { id: 8, name: "Green Olive", cuisine: "Turkish", location: "Queens" }
  ];

  // Mock data for searchable cuisine types
  const cuisineOptions = [
    { value: 'middle-eastern', label: 'Middle Eastern', icon: '🥙' },
    { value: 'turkish', label: 'Turkish', icon: '🍢' },
    { value: 'pakistani', label: 'Pakistani', icon: '🍛' },
    { value: 'american', label: 'American', icon: '🍔' },
    { value: 'mexican', label: 'Mexican', icon: '🌮' },
    { value: 'lebanese', label: 'Lebanese', icon: '🥗' },
    { value: 'mediterranean', label: 'Mediterranean', icon: '🫒' },
    { value: 'indian', label: 'Indian', icon: '🍲' },
    { value: 'italian', label: 'Italian', icon: '🍝' },
    { value: 'japanese', label: 'Japanese', icon: '🍣' }
  ];

  // Mock data for locations
  const locationOptions = [
    { value: 'near-me', label: 'Near Me (Use Current Location)', icon: '📍' },
    { value: 'new-york', label: 'New York, NY', icon: '🏙️' },
    { value: 'brooklyn', label: 'Brooklyn, NY', icon: '🌉' },
    { value: 'queens', label: 'Queens, NY', icon: '🏘️' },
    { value: 'manhattan', label: 'Manhattan, NY', icon: '🗽' },
    { value: 'los-angeles', label: 'Los Angeles, CA', icon: '🌴' },
    { value: 'chicago', label: 'Chicago, IL', icon: '🏢' },
    { value: 'houston', label: 'Houston, TX', icon: '🚀' }
  ];

  // Custom styles for React-Select
  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      width: '100%',
      backgroundColor: 'white',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
      border: '1px solid #ddd',
      marginTop: '4px'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px 0',
    }),
    control: (provided) => ({
      ...provided,
      border: '1px solid #0D9488',
      borderRadius: '4px',
      minHeight: '48px',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #0D9488',
      },
      backgroundColor: 'white',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#26958b' : state.isFocused ? '#e6f7f5' : 'white',
      color: state.isSelected ? 'white' : '#333',
      padding: '10px 15px',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#333',
    }),
    input: (provided) => ({
      ...provided,
      color: '#333',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
  };

  // Custom option components with icons
  const CuisineOption = ({ innerProps, label, data }) => (
    <div {...innerProps} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
      <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>{data.icon}</span>
      <span style={{ color: '#333' }}>{label}</span>
    </div>
  );

  const LocationOption = ({ innerProps, label, data }) => (
    <div {...innerProps} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
      <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>{data.icon}</span>
      <span style={{ color: '#333' }}>{label}</span>
    </div>
  );

  // Handle "use current location"
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // In a real app, you would use these coordinates to find nearby restaurants
          console.log("Latitude:", position.coords.latitude);
          console.log("Longitude:", position.coords.longitude);
          
          // For demonstration, we'll just set the location to "Near Me"
          setSearchLocation({ value: 'near-me', label: 'Near Me (Current Location)', icon: '📍' });
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Unable to access your location. Please select a location manually.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser. Please select a location manually.");
    }
  };

  const handleSearch = () => {
    // Filter restaurants based on search query and location
    let results = mockRestaurants;
    
    if (searchQuery) {
      const queryLower = searchQuery.value 
        ? searchQuery.label.toLowerCase() 
        : typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
        
      results = results.filter(restaurant => 
        restaurant.name.toLowerCase().includes(queryLower) || 
        restaurant.cuisine.toLowerCase().includes(queryLower)
      );
    }
    
    if (searchLocation) {
      // Special case for "Near Me"
      if (searchLocation.value === 'near-me') {
        // In a real app, you would use geolocation data to find truly nearby restaurants
        // For demo purposes, we'll just use a subset of restaurants
        results = results.slice(0, 4);
      } else {
        const locationStr = searchLocation.label.split(',')[0].toLowerCase();
        results = results.filter(restaurant =>
          restaurant.location.toLowerCase().includes(locationStr)
        );
      }
    }
    
    // Store search results in sessionStorage
    sessionStorage.setItem('searchResults', JSON.stringify(results));
    sessionStorage.setItem('searchQuery', JSON.stringify(searchQuery));
    sessionStorage.setItem('searchLocation', JSON.stringify(searchLocation));
    
    // Navigate to the search results page
    navigate('/restaurants');
  };

  const featuredRestaurants = [
    {
      id: 1,
      name: "Al-Madina Grill",
      image: "https://images.unsplash.com/photo-1606787366850-de6330128bfc?q=80&w=2070",
      cuisine: "Middle Eastern",
      price: "$$",
      rating: 4.5,
      reviews: 120,
      location: "New York, NY"
    },
    {
      id: 2,
      name: "Pasta Paradise",
      image: "https://images.unsplash.com/photo-1520201163981-8cc95007dd2a?q=80&w=2070",
      cuisine: "Italian",
      price: "$$$",
      rating: 4.7,
      reviews: 95,
      location: "Los Angeles, CA"
    },
    {
      id: 3,
      name: "Sushi Haven",
      image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=80&w=2070",
      cuisine: "Japanese",
      price: "$$$$",
      rating: 4.8,
      reviews: 150,
      location: "Chicago, IL"
    },
    {
      id: 4,
      name: "Taco Fiesta",
      image: "https://images.unsplash.com/photo-1520201163981-8cc95007dd2a?q=80&w=2070",
      cuisine: "Mexican",
      price: "$",
      rating: 4.3,
      reviews: 80,
      location: "Houston, TX"
    }
  ];

  const features = [
    {
      icon: <Tag className="w-4 h-4 text-success" />,
      title: "Exclusive benefits",
      description: "Offers for many restaurants and lots of other benefits with our loyalty program."
    },
    {
      icon: <Medal className="w-4 h-4 text-success" />,
      title: "Best choice",
      description: "An unrivaled selection of restaurants for whatever you want"
    },
    {
      icon: <Star className="w-4 h-4 text-success" />,
      title: "User reviews",
      description: "Recommendations and reviews from a powerful community."
    },
    {
      icon: <Calendar className="w-4 h-4 text-success" />,
      title: "Easy reservation",
      description: "Instant, free, everywhere. 24/7"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section text-white">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h1 className="display-4 fw-bold mb-4">Find Halal Restaurants Near You</h1>
              <p className="lead mb-5">Discover and book the best Halal restaurants in your area</p>
              
             <div className="search-box shadow" style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px' }}>
  <div className="row g-3">
    {/* Cuisine Dropdown */}
    <div className="col-md-5">
      <Select
        placeholder="cuisine or restaurant..."
        options={cuisineOptions}
        styles={{
          ...customStyles,
          menuPortal: base => ({ ...base, zIndex: 9999 }) // Ensures dropdown appears on top
        }}
        menuPortalTarget={document.body} // Moves dropdown to body
        menuPosition="fixed"
        components={{ 
          Option: CuisineOption,
          SingleValue: ({ children, ...props }) => (
            <components.SingleValue {...props}>
              <span style={{ color: '#333' }}>{children}</span>
            </components.SingleValue>
          )
        }}
        onChange={setSearchQuery}
        isClearable
        className="select-container"
        classNamePrefix="select"
      />
    </div>

    {/* Location Dropdown */}
    <div className="col-md-5">
      <Select
        placeholder="Select location..."
        options={locationOptions}
        styles={{
          ...customStyles,
          menuPortal: base => ({ ...base, zIndex: 9999 }) // Ensures dropdown appears on top
        }}
        menuPortalTarget={document.body} // Moves dropdown to body
        menuPosition="fixed"
        components={{ 
          Option: LocationOption,
          SingleValue: ({ children, ...props }) => (
            <components.SingleValue {...props}>
              <span style={{ color: '#333' }}>{children}</span>
            </components.SingleValue>
          )
        }}
        onChange={setSearchLocation}
        value={searchLocation}
        isClearable
        className="select-container"
        classNamePrefix="select"
      />
      
      {searchLocation && searchLocation.value === 'near-me' && (
        <div className="mt-2 text-dark small">
          <button onClick={handleUseCurrentLocation} className="btn btn-sm btn-outline-secondary">
            <i className="bi bi-geo-alt me-1"></i>
            Use my current location
          </button>
        </div>
      )}
    </div>

    {/* Search Button */}
    <div className="col-md-2">
      <button 
        className="btn btn-lg btn-primary w-100 h-100"
        onClick={handleSearch}
        style={{ backgroundColor: '#26958b', borderColor: '#26958b' }}
      >
        Search
      </button>
    </div>
  </div>
</div>

            </div>
          </div>
        </div>
      </section>

      {/* Featured Restaurants */}
      <section className="py-5 bg-light">
  <div className="container">
    <div className="row mb-4 align-items-center">
      <div className="col-md-6">
        <h2 className="fw-bold">Featured Restaurants</h2>
      </div>
      <div className="col-md-6 text-md-end">
        <Link to="/restaurants" className="btn btn-outline-primary">View All Restaurants</Link>
      </div>
    </div>
    <div className="row g-4">
      {featuredRestaurants.map(restaurant => (
        <div key={restaurant.id} className="col-md-6 col-lg-3">
          <Link 
            to={`/restaurants/${restaurant.id}`} 
            className="text-decoration-none text-dark"
          >
            <div className="restaurant-card card h-100">
              <img
                src={restaurant.image}
                className="restaurant-img card-img-top"
                alt={restaurant.name}
                style={{ height: "200px", objectFit: "cover" }}
              />
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h5 className="card-title mb-0">{restaurant.name}</h5>
                  <span className="rating-badge">
                    {restaurant.rating} ★
                  </span>
                </div>
                <p className="text-muted mb-2">
                  {restaurant.cuisine} • {restaurant.price}
                </p>
                <p className="text-muted small mb-0">
                  <i className="bi bi-geo-alt me-1"></i>
                  {restaurant.location}
                </p>
                <div className="mt-3">
                  <span className="text-muted small">
                    {restaurant.reviews} reviews
                  </span>
                </div>
              </div>
              <div className="card-footer bg-white border-top-0">
                <Link 
                  to={`/restaurants/${restaurant.id}`} 
                  className="btn btn-outline-primary w-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  View Details
                </Link>
              </div>
            </div>
          </Link>
        </div>
      ))}
    </div>
  </div>
</section>
      {/* Most Booked of the Month */}
      <section className="py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="fw-bold m-0">Most booked of the month</h2>
            <div>
              <Link to="/restaurants" className="text-primary text-decoration-none">
                SEE ALL
              </Link>
            </div>
          </div>

          <div id="restaurantCarousel" className="carousel slide" data-bs-ride="carousel">
            <div className="carousel-inner">
              <div className="carousel-item active">
                <div className="row g-4">
                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/id" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070" 
                          className="card-img-top" 
                          alt="Au Bourguignon du Marais"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">French</span>
                            <span className="badge bg-success">9.4</span>
                          </div>
                          <h5 className="card-title mb-2">Au Bourguignon du Marais</h5>
                          <p className="text-muted mb-2">75004, Paris</p>
                          <p className="text-muted mb-0">€49 average price</p>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/2" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1603073163308-9654c3fb70b5?q=80&w=2070" 
                          className="card-img-top" 
                          alt="Terry's Café"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">French</span>
                            <span className="badge bg-success">8.7</span>
                          </div>
                          <h5 className="card-title mb-2">Terry's Café</h5>
                          <p className="text-muted mb-2">75015, Paris</p>
                          <p className="text-muted mb-2">€19 average price</p>
                          <span className="badge bg-dark">Up to -50%</span>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/3" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1544148103-0773bf10d330?q=80&w=2070" 
                          className="card-img-top" 
                          alt="Les Rupins"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">French</span>
                            <span className="badge bg-success">9.3</span>
                          </div>
                          <h5 className="card-title mb-2">Les Rupins</h5>
                          <p className="text-muted mb-2">75010, Paris</p>
                          <p className="text-muted mb-2">€28 average price</p>
                          <span className="badge bg-dark">Up to -50%</span>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/4" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070" 
                          className="card-img-top" 
                          alt="Le Ciel de Paris"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">French</span>
                            <span className="badge bg-success">8.4</span>
                          </div>
                          <h5 className="card-title mb-2">Le Ciel de Paris</h5>
                          <p className="text-muted mb-2">75015, Paris</p>
                          <p className="text-muted mb-2">€105 average price</p>
                          <span className="badge bg-dark">Up to -20%</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="carousel-item">
                <div className="row g-4">
                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/5" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074" 
                          className="card-img-top" 
                          alt="Gusti Gusto"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">Italian</span>
                            <span className="badge bg-success">9.0</span>
                          </div>
                          <h5 className="card-title mb-2">Gusti Gusto</h5>
                          <p className="text-muted mb-2">75017, Paris</p>
                          <p className="text-muted mb-2">€23 average price</p>
                          <span className="badge bg-dark">Up to -20%</span>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/6" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1579617487912-9ecc0bb85e99?q=80&w=2069" 
                          className="card-img-top" 
                          alt="Bon Amis"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">French</span>
                            <span className="badge bg-success">9.1</span>
                          </div>
                          <h5 className="card-title mb-2">Bon Amis</h5>
                          <p className="text-muted mb-2">75014, Paris</p>
                          <p className="text-muted mb-2">€25 average price</p>
                          <span className="badge bg-dark">Up to -50%</span>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/7" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1553621042-f6e147245754?q=80&w=2025" 
                          className="card-img-top" 
                          alt="Wrap'N Roll"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">Japanese</span>
                            <span className="badge bg-success">9.3</span>
                          </div>
                          <h5 className="card-title mb-2">Wrap'N Roll</h5>
                          <p className="text-muted mb-2">75009, Paris</p>
                          <p className="text-muted mb-2">€15 average price</p>
                        </div>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <Link to="/restaurants/8" className="text-decoration-none">
                      <div className="card h-100 border-0 shadow-sm">
                        <img 
                          src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=2047" 
                          className="card-img-top" 
                          alt="Café Courtial"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="text-muted">French</span>
                            <span className="badge bg-success">9.2</span>
                          </div>
                          <h5 className="card-title mb-2">Café Courtial</h5>
                          <p className="text-muted mb-2">75001, Paris</p>
                          <p className="text-muted mb-2">€26 average price</p>
                          <span className="badge bg-dark">Up to -30%</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Cities Section */}
      <section className="py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="fw-bold m-0">Other cities in The Halla-bees top cities around the world</h2>
          </div>

          <div className="position-relative">
            <div className="row g-3">
              <div className="col-6 col-md-4 col-lg-2">
                <Link to="/city/paris" className="text-decoration-none">
                  <div className="card border-0 rounded-3 overflow-hidden shadow-sm">
                    <img 
                      src="https://images.unsplash.com/photo-1499856871958-5b9627545d1a?q=80&w=2070" 
                      className="card-img" 
                      alt="Paris"
                      style={{ height: "180px", objectFit: "cover" }}
                    />
                    <div className="card-img-overlay d-flex align-items-end p-2">
                      <h5 className="card-title text-white fw-bold mb-0 text-shadow">Paris</h5>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <Link to="/city/amsterdam" className="text-decoration-none">
                  <div className="card border-0 rounded-3 overflow-hidden shadow-sm">
                    <img 
                      src="https://images.unsplash.com/photo-1534351590666-13e3e96b5017?q=80&w=2070" 
                      className="card-img" 
                      alt="Amsterdam"
                      style={{ height: "180px", objectFit: "cover" }}
                    />
                    <div className="card-img-overlay d-flex align-items-end p-2">
                      <h5 className="card-title text-white fw-bold mb-0 text-shadow">Amsterdam</h5>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <Link to="/city/barcelona" className="text-decoration-none">
                  <div className="card border-0 rounded-3 overflow-hidden shadow-sm">
                    <img 
                      src="https://images.unsplash.com/photo-1539037116277-4db20889f2d4?q=80&w=2070" 
                      className="card-img" 
                      alt="Barcelona"
                      style={{ height: "180px", objectFit: "cover" }}
                    />
                    <div className="card-img-overlay d-flex align-items-end p-2">
                      <h5 className="card-title text-white fw-bold mb-0 text-shadow">Barcelona</h5>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <Link to="/city/lisbon" className="text-decoration-none">
                  <div className="card border-0 rounded-3 overflow-hidden shadow-sm">
                    <img 
                      src="https://images.unsplash.com/photo-1585208798174-6cedd86e019a?q=80&w=2073" 
                      className="card-img" 
                      alt="Lisbon"
                      style={{ height: "180px", objectFit: "cover" }}
                    />
                    <div className="card-img-overlay d-flex align-items-end p-2">
                      <h5 className="card-title text-white fw-bold mb-0 text-shadow">Lisbon</h5>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <Link to="/city/madrid" className="text-decoration-none">
                  <div className="card border-0 rounded-3 overflow-hidden shadow-sm">
                    <img 
                      src="https://images.unsplash.com/photo-1543783207-ec64e4d95325?q=80&w=2070" 
                      className="card-img" 
                      alt="Madrid"
                      style={{ height: "180px", objectFit: "cover" }}
                    />
                    <div className="card-img-overlay d-flex align-items-end p-2">
                      <h5 className="card-title text-white fw-bold mb-0 text-shadow">Madrid</h5>
                    </div>
                  </div>
                </Link>
              </div>

              <div className="col-6 col-md-4 col-lg-2">
                <Link to="/city/milan" className="text-decoration-none">
                  <div className="card border-0 rounded-3 overflow-hidden shadow-sm">
                    <img 
                      src="https://images.unsplash.com/photo-1499856871958-5b9627545d1a?q=80&w=2070" 
                      className="card-img" 
                      alt="Milan"
                      style={{ height: "180px", objectFit: "cover" }}
                    />
                    <div className="card-img-overlay d-flex align-items-end p-2">
                      <h5 className="card-title text-white fw-bold mb-0 text-shadow">Milan</h5>
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            <button className="btn btn-light rounded-circle position-absolute top-50 end-0 translate-middle-y" style={{ zIndex: 10 }}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>

        <style jsx>{`
          .text-shadow {
            text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
          }
        `}</style>
      </section>

      {/* How It Works Section */}
      <section className="py-5">
        <div className="container">
          <h2 className="fw-bold mb-4">How does it work?</h2>
          
          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <div className="card border h-100 p-4 hover-shadow">
                  <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: "48px", height: "48px" }}>
                    {feature.icon}
                  </div>
                  <h3 className="h5 fw-bold mb-2">{feature.title}</h3>
                  <p className="text-muted">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Discover Top 100 Section */}
      <section className="py-4">
        <div className="container">
          <div className="card border-0 shadow-sm">
            <div 
              className="row g-0 align-items-center"
              style={{
                background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))',
                borderRadius: '0.5rem',
                overflow: 'hidden'
              }}
            >
              <div className="col-md-7 p-4">
                <div className="ps-md-3">
                  <h2 className="h3 fw-bold text-white mb-1">Discover the Top 100</h2>
                  <h3 className="h4 fw-bold text-white mb-3">Paris</h3>
                  <div>
                    <Link to="/top-restaurants" className="text-white text-decoration-none">
                      <span className="border-bottom border-1 pb-1 small">SEE FULL LIST <i className="bi bi-arrow-right ms-1"></i></span>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="col-md-5 p-0">
                <img 
                  src="https://images.unsplash.com/photo-1607013251379-e6eecfffe234?q=80&w=1887&auto=format&fit=crop" 
                  alt="Gourmet dish" 
                  className="img-fluid"
                  style={{ 
                    height: '180px', 
                    width: '100%', 
                    objectFit: 'cover' 
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Owner Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 mb-4 mb-lg-0">
              <h2 className="fw-bold mb-4">Are you a restaurant owner?</h2>
              <img 
                src="https://images.unsplash.com/photo-1577219491135-ce391730fb2c?q=80&w=2070" 
                className="img-fluid rounded-3 shadow-sm" 
                alt="Chef preparing food"
                style={{ maxHeight: "360px", objectFit: "cover", width: "100%" }}
              />
            </div>
            <div className="col-lg-7">
              <div className="card border-0 shadow-sm rounded-3 p-4">
                <div className="card-body">
                  <h3 className="fw-bold mb-2">Register your Restaurant</h3>
                  <p className="text-muted mb-4">Tell us more about you and we will contact you as soon as possible</p>
                  <Link to="/register-restaurant" className="btn btn-outline-success px-4 mb-4">
                    SEE MORE INFORMATION
                  </Link>

                  <hr className="my-4" />

                  <h3 className="fw-bold mb-2">Already a Client</h3>
                  <p className="text-muted mb-4">Log in to Manager and contact us by Chat.</p>
                  <Link to="/login" className="btn btn-outline-success px-4">
                    LOG IN TO MANAGER
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;